import { useEffect, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import SectionCard from "../SectionCard"
import { Button } from "../../ui"
import { useRefined } from "../../ui/refined"
import ActionNotes from "../ActionNotes"
import { DeadlineField, useDeadlineFields, buildDeadlineParam } from "../DeadlineField"
import { formatVND } from "../../constants"

// Right-column decision surface for the Order Review screen (manager only).
// Buttons are driven ENTIRELY by `allowedActions` from the backend — never
// hardcoded. The card is controlled: it reports the picked action up via
// `onPick`, and the container owns the confirmation dialog + submission (so
// the mobile sticky bar can share the exact same flow).

export const ACTION_META = {
  approve: {
    label: "Approve",
    color: "success",
    variant: "gradient",
    icon: "check_circle",
    title: "Approve this order",
    confirm: "Approve",
    noteRequired: false,
    placeholder: "Add a note (optional)",
  },
  reject: {
    label: "Reject",
    color: "error",
    variant: "outlined",
    icon: "cancel",
    title: "Reject this order",
    confirm: "Reject",
    noteRequired: true,
    placeholder: "Reason for rejection",
  },
  info_request: {
    label: "Request Info",
    color: "warning",
    variant: "outlined",
    icon: "help",
    title: "Request more information",
    confirm: "Send request",
    noteRequired: true,
    placeholder: "What do you need from the requester?",
  },
}

export const ORDERED = ["approve", "reject", "info_request"]

export const allowed = (actions = []) => ORDERED.filter((a) => actions.includes(a))

// Bare buttons — reused by the card and the mobile sticky bar.
// Outlined (non-primary) decisions keep their meaning through a tinted outline
// on refined pages: destructive = red, request-info = amber.
const REFINED_TONE_CLASS = { error: "pc-btn--danger-outline", warning: "pc-btn--warn-outline" }

export const DecisionButtons = ({ actions, disabled, onPick, block = true }) => {
  const refined = useRefined()
  return (
    <MDBox display="flex" flexDirection={block ? "column" : "row"} gap={1} flex={block ? undefined : 1}>
      {actions.map((key) => {
        const meta = ACTION_META[key]
        if (!meta) return null
        if (refined) {
          return (
            <Button
              key={key}
              block={block}
              style={block ? undefined : { flex: 1 }}
              variant={meta.variant === "gradient" ? "primary" : "secondary"}
              className={(meta.variant === "outlined" && REFINED_TONE_CLASS[meta.color]) || ""}
              leftIcon={<Icon>{meta.icon}</Icon>}
              disabled={disabled}
              onClick={() => onPick(key)}
            >
              {meta.label}
            </Button>
          )
        }
        return (
          <MDButton
            key={key}
            fullWidth={block}
            sx={block ? undefined : { flex: 1 }}
            variant={meta.variant}
            color={meta.color}
            disabled={disabled}
            onClick={() => onPick(key)}
          >
            <Icon sx={{ mr: 0.75 }}>{meta.icon}</Icon>
            {meta.label}
          </MDButton>
        )
      })}
    </MDBox>
  )
}

export const DecisionCard = ({ allowedActions = [], disabled = false, statusMessage, onPick, notes }) => {
  const actions = allowed(allowedActions)
  return (
    <SectionCard title="Decision" icon="gavel" mb={0}>
      {disabled ? (
        <MDBox textAlign="center" py={1}>
          <MDBox
            width="2.75rem"
            height="2.75rem"
            borderRadius="50%"
            bgColor="grey-100"
            color="text"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            mb={1.5}
          >
            <Icon>lock</Icon>
          </MDBox>
          <MDTypography variant="body2" color="text">
            You can no longer act on this order.
          </MDTypography>
        </MDBox>
      ) : actions.length > 0 ? (
        <DecisionButtons actions={actions} onPick={onPick} />
      ) : (
        <MDBox textAlign="center" py={1}>
          <MDBox
            width="2.75rem"
            height="2.75rem"
            borderRadius="50%"
            bgColor="grey-100"
            color="success"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            mb={1.5}
          >
            <Icon>check_circle</Icon>
          </MDBox>
          <MDTypography variant="body2" color="text">
            {statusMessage || "There's nothing for you to act on right now."}
          </MDTypography>
        </MDBox>
      )}
      <ActionNotes notes={notes} />
    </SectionCard>
  )
}

// Confirmation dialog for a single picked action. `busy` blocks double-submit.
export const ConfirmModal = ({ actionKey, order, busy, onCancel, onSubmit }) => {
  const meta = ACTION_META[actionKey]
  const [note, setNote] = useState("")
  const { date, setDate, time, setTime } = useDeadlineFields(actionKey)

  // Reset the note whenever a different action is opened.
  useEffect(() => setNote(""), [actionKey])

  if (!meta) return null
  const invalid = meta.noteRequired && !note.trim()
  const category = order?.categoryName || order?.category || "this order"

  return (
    <Dialog open onClose={() => !busy && onCancel()} fullWidth maxWidth="sm">
      <DialogTitle>{meta.title}</DialogTitle>
      <DialogContent>
        <MDBox pt={1} display="flex" flexDirection="column" gap={2}>
          {actionKey === "approve" && (
            <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
              <MDTypography variant="button" color="text">
                Approving <strong>{formatVND(order?.total)}</strong> for {category}
                {order?.orCode ? ` (${order.orCode})` : ""}.
              </MDTypography>
            </MDBox>
          )}
          <MDInput
            fullWidth
            multiline
            rows={4}
            labelType="non-sticky"
            labelText="Note"
            required={meta.noteRequired}
            placeholder={meta.placeholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
          {actionKey === "info_request" && (
            <DeadlineField
              date={date}
              time={time}
              onChangeDate={setDate}
              onChangeTime={setTime}
              disabled={busy}
            />
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color={meta.color}
          onClick={() => onSubmit(note.trim(), buildDeadlineParam(date, time))}
          disabled={busy || invalid}
        >
          {busy ? "Submitting…" : meta.confirm}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}

export default DecisionCard
